// src/layouts/CustomLayout.tsx
//
// v3 "Pattern A" shell: the rail spans the full viewport height flush to the
// edges, and the app bar is absolutely positioned over the content column
// only (see CustomAppBar). Desktop locks the viewport height and hides
// overflow (the menu/content each own their own internal scroll), while
// mobile keeps its natural document scroll with a fixed-position app bar
// offset. The `sm` breakpoint matches react-admin's own Sidebar
// drawer-vs-docked switch (see CustomMenu).
import React from 'react';
import { Layout, LayoutProps } from 'react-admin';
import { useTheme, useMediaQuery } from '@mui/material';
import { CustomAppBar } from './CustomAppBar';
import { CustomMenu } from './CustomMenu';

export const CustomLayout: React.FC<LayoutProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Layout
      {...props}
      appBar={CustomAppBar}
      menu={CustomMenu}
      sx={{
        '&': {
          backgroundColor: theme.palette.background.default,
          // react-admin's Layout root ships `min-width: fit-content`, which
          // lets any wide intrinsic content (e.g. a long MuiTabs row inside
          // a config form) stretch the whole shell past the viewport and
          // produce a page-level horizontal scrollbar. Wide content must
          // scroll inside its own container, never the page body.
          minWidth: 0,
        },

        ...(!isMobile && {
          height: '100vh',
          overflow: 'hidden',
        }),

        '& .RaLayout-appFrame': {
          marginTop: 0,
          alignItems: 'flex-start',
          // Anchor for the desktop AppBar, which is absolutely positioned
          // over the content column only (see CustomAppBar).
          ...(!isMobile && { height: '100%', position: 'relative' }),
        },

        // Desktop: the react-admin Sidebar renders as a docked MUI Drawer —
        //   .RaSidebar-docked > .RaSidebar-paper > .RaSidebar-fixed > our rail Box.
        // (There is NO `.RaSidebar-root` in react-admin 5 — earlier rules
        // targeting it silently matched nothing.) The Drawer paper ships its
        // own overflow/height, which clipped the 100vh rail at the foot (the
        // profile card). Force the chain to full height and kill the
        // paper's scroll so the rail reaches the bottom edge.
        '& .RaSidebar-docked': {
          margin: '0 !important',
          padding: '0 !important',
          ...(!isMobile && { height: '100vh' }),
        },
        '& .RaSidebar-paper': {
          border: 'none',
          backgroundColor: 'transparent',
          margin: '0 !important',
          padding: '0 !important',
          ...(!isMobile && {
            position: 'relative',
            height: '100vh',
            overflow: 'visible',
          }),
        },
        '& .RaSidebar-fixed': {
          margin: '0 !important',
          ...(!isMobile && {
            position: 'relative !important',
            height: '100vh !important',
            overflow: 'visible !important',
          }),
        },

        '& .RaLayout-contentWithSidebar': {
          display: 'flex',
          flex: 1,
          gap: 0,
          width: '100%',
          // Pattern A: no outer padding — the rail is flush to the left/top/
          // bottom edges and the row spans the full viewport height (the
          // AppBar is absolute, so this row starts at y=0).
          ...(!isMobile && {
            padding: 0,
            height: '100vh',
            overflow: 'hidden',
          }),
        },

        '& .RaLayout-content': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,

          ...(isMobile && {
            height: 'calc(100dvh - 52px)',
            paddingTop: '52px',
            paddingBottom: 0,
            overflow: 'hidden',
          }),

          ...(!isMobile && {
            height: '100vh',
            // Reserve the top strip for the absolute AppBar, plus the
            // uniform administrative-app page padding (24px) as the single
            // "air" every screen gets. This lives here (a border-box frame)
            // rather than on the scroll container below, so the padding
            // never overflows it.
            paddingTop: `calc(52px + ${theme.spacing(3)})`,
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingBottom: theme.spacing(3),
            boxSizing: 'border-box',
            overflow: 'hidden',
          }),
        },

        '& .RaLayout-content > div': {
          width: '100%',
          backgroundColor: theme.palette.background.default,
          WebkitOverflowScrolling: 'touch',
          // This element is the single vertical scroll region AND hosts each
          // screen's sticky header. A top padding here (the page wrapper's
          // own padding) pushes a `position:sticky; top:0` header's pin
          // point below the padding edge, opening a slit between the app
          // bar and the pinned header through which scrolled rows leak.
          // Zero the top padding so the header pins flush under the app
          // bar; the side and bottom insets are kept.
          paddingTop: 0,

          ...(!isMobile && {
            borderRadius: 0,
            boxShadow: 'none',
            overflow: 'auto',
            height: '100%',
          }),

          ...(isMobile && {
            flex: 1,
            minHeight: 0,
            borderRadius: 0,
            boxShadow: 'none',
            overflowY: 'auto',
            overflowX: 'hidden',
            maxWidth: '100vw',
          }),
        },
      }}
    />
  );
};

export default CustomLayout;
